import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, TrendingDown, AlertTriangle, Wallet, Loader2, RefreshCw } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Tresorerie() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState('realiste');
  const [config, setConfig] = useState({ seuil_alerte: 5000 });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/tresorerie/previsions`, { params: { scenario, horizon: 90 } }),
        axios.get(`${API_URL}/tresorerie/config`),
      ]);
      setData(pRes.data); setConfig(cRes.data);
    } catch (e) {} finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [scenario]);

  const chartData = data?.previsions?.filter(p => p.type !== 'solde' || p.source === 'initial').map(p => ({
    date: p.date, solde: p.solde_cumule, label: p.label,
  })) || [];

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold"><Wallet className="w-6 h-6 inline mr-2" />Trésorerie prévisionnelle</h1></div>
        <div className="flex gap-2">
          <Select value={scenario} onValueChange={setScenario}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="optimiste">Optimiste</SelectItem>
              <SelectItem value="realiste">Réaliste</SelectItem>
              <SelectItem value="pessimiste">Pessimiste</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAll}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Alerts */}
      {data?.alertes?.map((a, i) => (
        <Card key={i} className={`border-l-4 ${a.severity === 'critical' ? 'border-l-red-500 bg-red-50' : 'border-l-amber-500 bg-amber-50'}`}>
          <CardContent className="py-3 flex items-center gap-3">
            <AlertTriangle className={`w-5 h-5 ${a.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
            <p className="text-sm font-medium">{a.message}</p>
          </CardContent>
        </Card>
      ))}

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Solde actuel', value: data?.solde_actuel || 0, icon: Wallet, color: '#0F5156' },
          { label: 'Solde J+30', value: data?.solde_30j || 0, icon: data?.solde_30j >= 0 ? TrendingUp : TrendingDown, color: data?.solde_30j >= 0 ? '#10b981' : '#ef4444' },
          { label: 'Solde J+60', value: data?.solde_60j || 0, icon: data?.solde_60j >= 0 ? TrendingUp : TrendingDown, color: data?.solde_60j >= 0 ? '#10b981' : '#ef4444' },
          { label: 'Solde J+90', value: data?.solde_90j || 0, icon: data?.solde_90j >= 0 ? TrendingUp : TrendingDown, color: data?.solde_90j >= 0 ? '#10b981' : '#ef4444' },
        ].map((kpi, i) => (
          <Card key={i}><CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2"><kpi.icon className="w-4 h-4" style={{ color: kpi.color }} /><span className="text-sm text-slate-500">{kpi.label}</span></div>
            <p className="text-2xl font-bold" style={{ color: kpi.color }}>{formatCurrency(kpi.value)}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Chart */}
      <Card>
        <CardHeader><CardTitle>Évolution du solde prévisionnel</CardTitle><CardDescription>Scénario {scenario} sur 90 jours</CardDescription></CardHeader>
        <CardContent>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorSolde" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0F5156" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0F5156" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ borderRadius: 8 }} formatter={v => [formatCurrency(v), 'Solde']} />
                <ReferenceLine y={config.seuil_alerte} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `Seuil: ${config.seuil_alerte}€`, position: 'right', fontSize: 10, fill: '#ef4444' }} />
                <ReferenceLine y={0} stroke="#94a3b8" />
                <Area type="monotone" dataKey="solde" stroke="#0F5156" strokeWidth={2} fill="url(#colorSolde)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Recurring charges */}
      {data?.charges_recurrentes?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Charges récurrentes détectées</CardTitle><CardDescription>Basées sur l'historique des 6 derniers mois</CardDescription></CardHeader>
          <CardContent className="p-0">
            <Table><TableHeader><TableRow><TableHead>Libellé</TableHead><TableHead>Montant moyen</TableHead><TableHead>Occurrences</TableHead><TableHead>Jour habituel</TableHead></TableRow></TableHeader>
            <TableBody>{data.charges_recurrentes.map((c, i) => (
              <TableRow key={i}><TableCell className="font-medium">{c.label}</TableCell><TableCell className="font-mono text-red-600">{formatCurrency(-c.montant_moyen)}</TableCell><TableCell>{c.occurrences}x</TableCell><TableCell>~{c.jour_habituel} du mois</TableCell></TableRow>
            ))}</TableBody></Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
